// frontend/src/screens/AdminScreen.jsx
// Standalone admin panel — separate Basic Auth (not Cognito).
// Visit /admin in the URL to access (rendered by App.jsx based on path).

import { useState, useEffect, useCallback } from "react";
import { adminApi } from "../api/client";
import { C, Card } from "./theme";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { hour12: false }); }
  catch { return iso; }
};

const Section = ({ title, right, children }) => (
  <Card>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
      {right}
    </div>
    {children}
  </Card>
);

// ─── Login form ─────────────────────────────────────────────────────────────
function AdminLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      await adminApi.login(username, password, remember);
      onSuccess();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <form onSubmit={submit} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28, width: "100%", maxWidth: 360 }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>TrackFin Admin</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>System diagnostics + monitoring</div>

        <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>USERNAME</label>
        <input autoFocus value={username} onChange={e => setUsername(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: "inherit", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }} />

        <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>PASSWORD</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: "inherit", fontSize: 14, marginBottom: 14, boxSizing: "border-box" }} />

        <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6, marginBottom: 16, cursor: "pointer" }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
          Remember on this device
        </label>

        {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{err}</div>}

        <button type="submit" disabled={busy || !username || !password}
          style={{ width: "100%", padding: 11, background: C.accent, color: "#fff", border: "none", borderRadius: 24, fontWeight: 700, fontSize: 14, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Signing in..." : "Sign In"}
        </button>

        <div style={{ fontSize: 11, color: C.muted, marginTop: 16, textAlign: "center" }}>
          Not a regular user account — this is a separate admin login.
        </div>
      </form>
    </div>
  );
}

// ─── Logs viewer ────────────────────────────────────────────────────────────
function LogsViewer() {
  const [fn, setFn]       = useState("email-parser");
  const [hours, setHours] = useState(24);
  const [filter, setFilter] = useState("");
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");
  const [fns, setFns]     = useState([]);

  useEffect(() => {
    adminApi.lambdaFunctions().then(d => setFns(d.groups || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const d = await adminApi.lambdaLogs({ fn, hours, filter });
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [fn, hours, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Section title="Lambda Logs"
      right={
        <button onClick={load} disabled={busy}
          style={{ padding: "6px 14px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Loading..." : "Refresh"}
        </button>
      }>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <select value={fn} onChange={e => setFn(e.target.value)}
          style={{ padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
          {fns.length === 0 && <option value="email-parser">email-parser</option>}
          {fns.map(g => (
            <option key={g.name} value={g.name}>{g.name}</option>
          ))}
        </select>
        <select value={hours} onChange={e => setHours(Number(e.target.value))}
          style={{ padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
          <option value={1}>last 1h</option>
          <option value={6}>last 6h</option>
          <option value={24}>last 24h</option>
          <option value={72}>last 3 days</option>
          <option value={168}>last 7 days</option>
        </select>
        <input value={filter} onChange={e => setFilter(e.target.value)}
          placeholder='Filter (e.g. "ERROR", "TypeError", "userId")'
          style={{ flex: 1, minWidth: 200, padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }} />
      </div>

      {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{err}</div>}

      <div style={{ background: "#0a0f1a", color: "#cbd5e1", borderRadius: 6, padding: 12, fontFamily: "Menlo, Monaco, monospace", fontSize: 11, maxHeight: 480, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
        {data?.events?.length > 0 ? data.events.map((e, i) => (
          <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
            <div style={{ color: "#94a3b8", fontSize: 10 }}>{fmtTime(e.timestamp)} · {e.stream}</div>
            <div>{e.message}</div>
          </div>
        )) : (
          <div style={{ color: "#64748b" }}>{busy ? "Loading logs..." : "No matching log events."}</div>
        )}
      </div>
    </Section>
  );
}

// ─── Pending emails + reprocess ─────────────────────────────────────────────
function PendingEmails() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");
  const [reprocessing, setReprocessing] = useState(null);
  const [reprocessResult, setReprocessResult] = useState(null);

  const load = async () => {
    setBusy(true); setErr("");
    try { setData(await adminApi.pendingEmails()); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const reprocess = async (s3Key) => {
    setReprocessing(s3Key);
    setReprocessResult(null);
    try {
      const r = await adminApi.reprocessEmail(s3Key);
      setReprocessResult({ key: s3Key, ...r });
    } catch (e) {
      setReprocessResult({ key: s3Key, error: e.message });
    } finally {
      setReprocessing(null);
    }
  };

  return (
    <Section title="Inbound Email Bucket (last 7 days)"
      right={<button onClick={load} disabled={busy}
        style={{ padding: "6px 14px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Loading..." : "Refresh"}
      </button>}>
      {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{err}</div>}

      {data?.emails?.length > 0 ? (
        <div style={{ maxHeight: 360, overflow: "auto", border: `1px solid ${C.border}`, borderRadius: 6 }}>
          {data.emails.map(e => (
            <div key={e.key} style={{ padding: "10px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, fontSize: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.key}</div>
                <div style={{ color: C.muted, fontSize: 10 }}>{fmtTime(e.lastModified)} · {e.size} bytes</div>
              </div>
              <button onClick={() => reprocess(e.key)} disabled={reprocessing === e.key}
                style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.blue}`, color: C.blue, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {reprocessing === e.key ? "..." : "Reprocess"}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: C.muted, fontSize: 13 }}>No emails in bucket (last 7 days).</div>
      )}

      {reprocessResult && (
        <div style={{ marginTop: 10, padding: 10, background: reprocessResult.error ? C.red + "15" : C.success + "15", borderRadius: 6, fontSize: 11, fontFamily: "monospace", whiteSpace: "pre-wrap", maxHeight: 200, overflow: "auto" }}>
          <strong>{reprocessResult.key}</strong>{"\n"}
          {reprocessResult.error || JSON.stringify(reprocessResult, null, 2)}
        </div>
      )}
    </Section>
  );
}

// ─── Activity log viewer ─────────────────────────────────────────────────────
function ActivityLog() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try { setData(await adminApi.activity(50)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Section title={`Activity Log (${data?.count || 0})`}
      right={<button onClick={load} disabled={busy}
        style={{ padding: "6px 14px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Loading..." : "Refresh"}
      </button>}>
      {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{err}</div>}

      {data?.entries?.length > 0 ? (
        <div style={{ maxHeight: 480, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.surface, position: "sticky", top: 0 }}>
                {["Time", "User", "Action", "Resource", "Status"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontWeight: 700, color: C.muted, letterSpacing: 0.3, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{fmtTime(e.timestamp)}</td>
                  <td style={{ padding: "6px 10px", fontFamily: "monospace", fontSize: 10 }}>{(e.userId || "—").slice(0, 8)}</td>
                  <td style={{ padding: "6px 10px", fontWeight: 600 }}>{e.action || "—"}</td>
                  <td style={{ padding: "6px 10px" }}>{e.resourceType || ""} {e.resourceId ? `· ${e.resourceId}` : ""}</td>
                  <td style={{ padding: "6px 10px", color: (e.statusCode >= 400) ? C.red : C.success, fontWeight: 700 }}>{e.statusCode || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: C.muted, fontSize: 13 }}>{data?.note || "No activity entries yet."}</div>
      )}
    </Section>
  );
}

// ─── Main Admin Panel ────────────────────────────────────────────────────────
export default function AdminScreen() {
  const [authed, setAuthed] = useState(adminApi.isLoggedIn());
  const [tab, setTab]       = useState("logs");

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const TABS = [
    { id: "logs",     label: "Lambda Logs" },
    { id: "emails",   label: "Pending Emails" },
    { id: "activity", label: "Activity Log" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <div style={{ background: C.card, borderBottom: `1px solid ${C.border}`, padding: "12px 24px" }}>
        <div style={{ maxWidth: 1128, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>TrackFin <span style={{ color: C.accent }}>Admin</span></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding: "6px 12px", background: tab === t.id ? C.greyLight : "transparent", color: tab === t.id ? C.text : C.muted, border: "none", borderRadius: 6, cursor: "pointer", fontWeight: tab === t.id ? 700 : 500, fontSize: 13 }}>
                {t.label}
              </button>
            ))}
            <button onClick={() => { adminApi.logout(); setAuthed(false); }}
              style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1128, margin: "0 auto", padding: "24px", display: "flex", flexDirection: "column", gap: 16 }}>
        {tab === "logs"     && <LogsViewer />}
        {tab === "emails"   && <PendingEmails />}
        {tab === "activity" && <ActivityLog />}
      </div>
    </div>
  );
}
